/**
 * Display helpers for people. Masking is applied at display and is never a substitute
 * for access control (CON-03). The mask is a fixed shape so it does not leak name length.
 */
export function maskName(fullName: string): string {
  const first = fullName.trim().charAt(0).toUpperCase();
  return `${first}***** *******`;
}

export function fullName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

/** "Dr. Priya Raman" for physicians, the plain name for everyone else. */
export function displayName(p: { firstName: string; lastName: string; role: string }): string {
  return p.role === 'expert' ? `Dr. ${fullName(p)}` : fullName(p);
}

export function initials(p: { firstName: string; lastName: string }): string {
  return `${p.firstName.charAt(0)}${p.lastName.charAt(0)}`.toUpperCase();
}

/** "45 M", "63 F", or an empty string when demographics were not volunteered. */
export function ageSex(age: number | null, gender: string | null): string {
  const g = gender === 'female' ? 'F' : gender === 'male' ? 'M' : gender ? 'X' : '';
  return [age ?? '', g].filter((x) => x !== '').join(' ');
}

/** "45, male". */
export function ageSexLong(age: number | null, gender: string | null): string {
  return [age, gender].filter((x) => x !== null && x !== '').join(', ');
}

export function wordCount(text: string): number {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** "Dr. Priya Raman" → "PR"; "Anne Whitlock" → "AW". */
export function initialsOf(name: string): string {
  const parts = name
    .replace(/^Dr\.\s+/, '')
    .trim()
    .split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''}`.toUpperCase();
}
